// src/pages/Appli/AppliAdmin/Prospects/modals/EditProspectModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaSave } from "react-icons/fa";
import Button from "../../../../../components/Button/Button";
import { toast } from "react-toastify";

const STATUTS = [
  "Nouveau Prospect",
  "A Recontacter",
  "Contacté & en attente",
  "Prospect Qualifié",
  "En Test",
  "Client",
  "Client SAAS",
  "Perdu",
];

const EditProspectModal = ({ prospect, onClose, onSave, associations = [], products = [] }) => {
  const isOpen = !!prospect;
  const [formData, setFormData] = useState(prospect || {});
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(prospect);
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);
    return () => {
      root.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, prospect, onClose]);

  const handleOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.prenom) {
      toast.error("Nom et prénom sont requis.");
      return;
    }
    const patch = {
      ...formData,
      association_id: formData.association_id || null,
      interet_produit_id: formData.interet_produit_id || null,
    };
    onSave?.(patch);
    onClose?.();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm" onClick={handleOverlay} aria-modal role="dialog">
      <div ref={dialogRef} className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">Modifier un Prospect</h3>
            <p className="text-white/90 text-xs sm:text-sm">Mettez à jour les informations puis enregistrez.</p>
          </div>
          <button onClick={onClose} className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white" data-close>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nom" required>
              <input name="nom" value={formData.nom || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>
            <Field label="Prénom" required>
              <input name="prenom" value={formData.prenom || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>

            <Field label="Email">
              <input type="email" name="email" value={formData.email || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>
            <Field label="Téléphone">
              <input name="telephone" value={formData.telephone || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>

            <Field label="Association">
              <select name="association_id" value={formData.association_id || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2">
                <option value="">—</option>
                {associations.map((a) => (
                  <option key={a.id} value={a.id}>{a.nom_asso}</option>
                ))}
              </select>
            </Field>
            <Field label="Fonction">
              <input name="fonction" value={formData.fonction || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>

            <Field label="Produit d’intérêt">
              <select name="interet_produit_id" value={formData.interet_produit_id || ""} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2">
                <option value="">—</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.nom_app}</option>
                ))}
              </select>
            </Field>
            <Field label="Provenance">
              <select name="provenance" value={formData.provenance || "autre"} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2">
                <option value="mailing">Mailing</option>
                <option value="phoning">Phoning</option>
                <option value="linkedin">LinkedIn</option>
                <option value="facebook">Facebook</option>
                <option value="connaissance">Connaissance</option>
                <option value="parrainage">Parrainage</option>
                <option value="autre">Autre</option>
              </select>
            </Field>

            <Field label="Statut" required>
              <select name="statut" value={formData.statut || "Nouveau Prospect"} onChange={handleChange} required className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2">
                {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Date de contact">
              <input type="date" name="date_contact" value={(formData.date_contact || "").slice(0,10)} onChange={handleChange} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>

            <Field label="Notes" className="sm:col-span-2">
              <textarea name="notes" value={formData.notes || ""} onChange={handleChange} rows={4} className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2" />
            </Field>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button text="Annuler" onClick={onClose} className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700" />
              <Button text="Enregistrer" icon={FaSave} type="submit" className="!rounded-xl" />
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-pink-600">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default EditProspectModal;
